import { forwardRef, useEffect, useRef } from 'react'
import { cn } from '../lib/utils'
import { Textarea as ShadTextArea } from './base/textarea'
import { Label } from './label'

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  endIcon?: React.ReactNode
  className?: string
  autoExpand?: boolean
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, endIcon, className, value, onChange, autoExpand = false, ...props }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)

    useEffect(() => {
      if (!autoExpand) return

      const adjustHeight = () => {
        const textarea = textareaRef.current
        if (!textarea) return

        textarea.style.height = 'auto'
        textarea.style.height = `${textarea.scrollHeight}px`
      }

      adjustHeight()
    }, [value, autoExpand])

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (autoExpand) {
        const textarea = e.target
        textarea.style.height = 'auto'
        textarea.style.height = `${textarea.scrollHeight}px`
      }
      onChange?.(e)
    }

    return (
      <div className="flex flex-col gap-1.5 relative">
        {label && <Label>{label}</Label>}
        <ShadTextArea
          rows={autoExpand ? 1 : 4}
          {...props}
          ref={(element) => {
            textareaRef.current = element
            if (!ref) return
            if (typeof ref === 'function') ref(element)
            else ref.current = element
          }}
          value={value}
          onChange={handleChange}
          className={cn(
            'shadow-none',
            autoExpand && 'resize-none overflow-hidden',
            className,
            endIcon && 'pb-[28px]',
          )}
        />
        {endIcon && <div className={cn('absolute p-2 right-0 bottom-px')}>{endIcon}</div>}
      </div>
    )
  },
)

Textarea.displayName = 'Textarea'
